<!-- eslint-disable vue/no-v-html -->
<template>
    <VJoyPanel>
        <template #panel-title>
            {{ $t('projectclient.projectview.details.terms.title') }}
        </template>

        <template #panel-body>
            <VJoyWrapper v-if="isIntermediationMode" row-gap="2" direction="column" align="flex-start">
                <VJoyText preset="primary-450">
                    {{ $t('projectclient.projectview.details.terms.up.model.title') }}
                </VJoyText>

                <VJoyText preset="primary-400">
                    {{ $t('projectclient.projectview.details.terms.up.model.description') }}
                </VJoyText>
            </VJoyWrapper>

            <ContractTermsHistory :terms="allContractTerms" />

            <VJoyWrapper v-if="freelancerTerms" row-gap="2" direction="column" align="flex-start">
                <VJoyText preset="primary-450">
                    {{
                        $t('projectclient.projectview.details.terms.freelancer.terms.title', {0: escapeHtml(projectInformation.freelancer.firstName)})
                    }}
                </VJoyText>

                <VJoyText preset="primary-400">{{ freelancerTerms }}</VJoyText>
            </VJoyWrapper>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore} from '@common/stores/project.store';
    import {storeToRefs} from 'pinia';
    import {useAsyncData} from '#imports';
    import {escapeHtml} from '@malt/sanitize';
    import {VJoyWrapper, VJoyPanel, VJoyText} from '@maltjoy/core-vue';
    import type {
        AppliedContractTermsResourceContractTermsInner,
        ContractDirectModeResource,
        ContractTripartiteModeResource,
        ContractSubcontractingModeResource,
    } from '@api';
    import ContractTermsHistory, {type TermHistoryItem} from './ContractTermsHistory.vue';

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, terms} = storeToRefs(projectInformationStore);

    await useAsyncData(() => projectInformationStore.fetchTerms(projectInformation.value.projectId), {deep: true});

    const isIntermediationMode = computed(
        () =>
            projectInformation.value?.contract.invoicingMode === 'TRIPARTITE' ||
            projectInformation.value?.contract.invoicingMode === 'SUBCONTRACTING',
    );

    const isTripartiteTerm = (term: AppliedContractTermsResourceContractTermsInner) => term.invoicingMode?.type === 'TRIPARTITE';
    const isDirectTerm = (term: AppliedContractTermsResourceContractTermsInner) => term.invoicingMode?.type === 'DIRECT';
    const isSubcontractingTerm = (term: AppliedContractTermsResourceContractTermsInner) => term.invoicingMode?.type === 'SUBCONTRACTING';

    const freelancerTerms = computed(() => terms.value?.current?.freelancerTerms);

    const allContractTerms = computed<TermHistoryItem[]>(() => {
        const contractTerms = terms.value?.current?.contractTerms ?? [];
        const groupTerms: TermHistoryItem[] = [];

        contractTerms.forEach((term) => {
            if (isTripartiteTerm(term)) {
                const tripartiteTerm = term.invoicingMode as ContractTripartiteModeResource;
                if (tripartiteTerm.projectTerms) {
                    groupTerms.push({
                        type: 'TRIPARTITE',
                        url: tripartiteTerm.projectTerms.url ?? '',
                        freelancerAcceptedAt: term.freelancerAcceptedAt,
                        clientAcceptedAt: term.clientAcceptedAt,
                        isCurrent: false,
                    });
                }
            } else if (isDirectTerm(term) || isSubcontractingTerm(term)) {
                const invoicingMode = term.invoicingMode as ContractDirectModeResource | ContractSubcontractingModeResource;
                if (invoicingMode.clientTerms) {
                    groupTerms.push({
                        type: 'SUBCONTRACTING',
                        url: invoicingMode.clientTerms.url ?? '',
                        freelancerAcceptedAt: term.freelancerAcceptedAt,
                        clientAcceptedAt: term.clientAcceptedAt,
                        isCurrent: false,
                    });
                }
            }
        });

        if (groupTerms.length > 0) {
            // Last in history is the current applied one
            groupTerms[groupTerms.length - 1].isCurrent = true;
        }

        return groupTerms;
    });
</script>

<style lang="scss" scoped>
    .project-terms-and-conditions {
        display: flex;
        flex-direction: column;
        gap: var(--joy-core-spacing-6);

        &__section {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-3);
        }
    }
</style>
